<template>
  <div
    ref="rootEl"
    class="searchable-select"
    :class="{
      'searchable-select--open': menuOpen,
      'searchable-select--up': openUpward,
      'searchable-select--disabled': disabled,
      'searchable-select--loading': loading,
      'searchable-select--has-value': hasValue,
    }"
  >
    <label v-if="label" class="searchable-select__label">{{ label }}</label>

    <!-- Positioning context is the field only (not the label) so up/down gaps match. -->
    <div class="searchable-select__field">
      <div
        ref="controlEl"
        class="searchable-select__control"
        @mousedown="onControlMouseDown"
      >
        <input
          ref="inputEl"
          class="searchable-select__input"
          type="text"
          :value="inputValue"
          :placeholder="placeholderText"
          :disabled="disabled"
          autocomplete="off"
          spellcheck="false"
          @focus="onFocus"
          @input="onInput"
          @keydown="onKeydown"
        />

        <button
          v-if="clearable && hasValue && !disabled"
          type="button"
          class="searchable-select__icon searchable-select__clear"
          aria-label="Clear selection"
          @mousedown.prevent.stop="clearValue"
        >
          ×
        </button>

        <button
          type="button"
          class="searchable-select__icon searchable-select__chevron"
          :aria-label="menuOpen ? 'Close options' : 'Open options'"
          :disabled="disabled"
          @mousedown.prevent.stop="toggleMenu"
        >
          {{ loading ? '…' : '▾' }}
        </button>
      </div>

      <ul
        v-if="menuOpen && !disabled"
        ref="menuEl"
        class="searchable-select__menu"
        role="listbox"
        :style="{ maxHeight: `${menuMaxHeight}px` }"
      >
        <li v-if="filteredItems.length === 0" class="searchable-select__empty">
          No matches
        </li>
        <li
          v-for="(item, index) in filteredItems"
          :key="`${item}-${index}`"
          class="searchable-select__option"
          :class="{ 'searchable-select__option--active': index === activeIndex }"
          role="option"
          :aria-selected="isSelected(item)"
          @mousedown.prevent="selectItem(item)"
          @mouseenter="activeIndex = index"
        >
          {{ item }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

type SelectValue = string | number | null

const props = withDefaults(
  defineProps<{
    modelValue?: SelectValue
    items?: Array<string | number>
    label?: string
    placeholder?: string
    disabled?: boolean
    loading?: boolean
    clearable?: boolean
  }>(),
  {
    modelValue: null,
    items: () => [],
    label: '',
    placeholder: '',
    disabled: false,
    loading: false,
    clearable: true,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: SelectValue]
}>()

const rootEl = ref<HTMLElement | null>(null)
const controlEl = ref<HTMLElement | null>(null)
const inputEl = ref<HTMLInputElement | null>(null)
const menuEl = ref<HTMLElement | null>(null)
const menuOpen = ref(false)
const openUpward = ref(false)
const menuMaxHeight = ref(240)
const query = ref('')
const activeIndex = ref(0)

const hasValue = computed(
  () =>
    props.modelValue !== null &&
    props.modelValue !== undefined &&
    props.modelValue !== '',
)

const placeholderText = computed(() => {
  if (menuOpen.value) return props.placeholder || 'Type to search…'
  if (hasValue.value) return ''
  return props.placeholder || props.label || 'Select…'
})

const inputValue = computed(() => {
  if (menuOpen.value) return query.value
  if (hasValue.value) return String(props.modelValue)
  return ''
})

const filteredItems = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return props.items
  return props.items.filter(item => String(item).toLowerCase().includes(q))
})

watch(
  () => props.modelValue,
  () => {
    if (!menuOpen.value) query.value = ''
  },
)

watch(filteredItems, items => {
  if (activeIndex.value >= items.length) {
    activeIndex.value = Math.max(0, items.length - 1)
  }
})

const isSelected = (item: string | number) =>
  String(item) === String(props.modelValue)

const updateMenuPlacement = () => {
  if (!controlEl.value) return

  const rect = controlEl.value.getBoundingClientRect()
  const gap = 4
  const padding = 8
  const preferredHeight = 240
  const spaceBelow = window.innerHeight - rect.bottom - padding
  const spaceAbove = rect.top - padding

  // Default: below. Flip above only when below does not have enough room.
  const shouldOpenUp = spaceBelow < 160 && spaceAbove > spaceBelow
  openUpward.value = shouldOpenUp
  menuMaxHeight.value = Math.max(
    120,
    Math.min(preferredHeight, (shouldOpenUp ? spaceAbove : spaceBelow) - gap),
  )
}

const openMenu = async () => {
  if (props.disabled) return
  menuOpen.value = true
  query.value = ''
  activeIndex.value = Math.max(
    0,
    filteredItems.value.findIndex(item => isSelected(item)),
  )
  updateMenuPlacement()
  await nextTick()
  updateMenuPlacement()
  inputEl.value?.focus()
}

const closeMenu = () => {
  menuOpen.value = false
  openUpward.value = false
  query.value = ''
  activeIndex.value = 0
}

const toggleMenu = () => {
  if (props.disabled) return
  if (menuOpen.value) closeMenu()
  else void openMenu()
}

const onControlMouseDown = (event: MouseEvent) => {
  if (props.disabled) return
  const target = event.target as HTMLElement | null
  if (
    target?.closest('.searchable-select__clear, .searchable-select__chevron')
  ) {
    return
  }
  event.preventDefault()
  if (!menuOpen.value) void openMenu()
  else inputEl.value?.focus()
}

const selectItem = (item: string | number) => {
  emit('update:modelValue', item)
  closeMenu()
}

const clearValue = () => {
  emit('update:modelValue', null)
  query.value = ''
  void openMenu()
}

const onFocus = () => {
  if (!menuOpen.value) void openMenu()
}

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  query.value = target.value
  menuOpen.value = true
  activeIndex.value = 0
  updateMenuPlacement()
}

const onKeydown = (event: KeyboardEvent) => {
  if (props.disabled) return

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!menuOpen.value) {
      void openMenu()
      return
    }
    activeIndex.value = Math.min(
      activeIndex.value + 1,
      filteredItems.value.length - 1,
    )
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    activeIndex.value = Math.max(activeIndex.value - 1, 0)
  } else if (event.key === 'Enter') {
    event.preventDefault()
    const item = filteredItems.value[activeIndex.value]
    if (menuOpen.value && item !== undefined) selectItem(item)
    else void openMenu()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    closeMenu()
    inputEl.value?.blur()
  }
}

const onDocumentPointerDown = (event: Event) => {
  if (!rootEl.value) return
  const path =
    typeof event.composedPath === 'function' ? event.composedPath() : []
  if (path.includes(rootEl.value)) return
  closeMenu()
}

const onReposition = () => {
  if (menuOpen.value) updateMenuPlacement()
}

onMounted(() => {
  document.addEventListener('mousedown', onDocumentPointerDown, true)
  window.addEventListener('resize', onReposition)
  window.addEventListener('scroll', onReposition, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentPointerDown, true)
  window.removeEventListener('resize', onReposition)
  window.removeEventListener('scroll', onReposition, true)
})
</script>

<style scoped>
.searchable-select {
  width: 100%;
  font-family: inherit;
  margin-bottom: 4px;
  z-index: 1;
}

.searchable-select--open {
  z-index: 300;
}

.searchable-select__label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
}

.searchable-select__field {
  position: relative;
  width: 100%;
}

.searchable-select__control {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 48px;
  padding: 0 12px;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  background: #fff;
  cursor: text;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.searchable-select--open .searchable-select__control,
.searchable-select__control:focus-within {
  border-color: rgb(25, 118, 210);
  box-shadow: 0 0 0 1px rgb(25, 118, 210);
}

.searchable-select--disabled .searchable-select__control {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
}

.searchable-select__input {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  border: 0 !important;
  outline: none !important;
  box-shadow: none !important;
  background: transparent !important;
  padding: 10px 0 !important;
  margin: 0 !important;
  font: inherit !important;
  font-size: 1rem !important;
  line-height: 1.4 !important;
  color: rgba(0, 0, 0, 0.87) !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

.searchable-select__input::placeholder {
  color: rgba(0, 0, 0, 0.45);
}

.searchable-select__icon {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.54);
  font-size: 1rem;
  line-height: 1;
  user-select: none;
}

.searchable-select__clear,
.searchable-select__chevron {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0 2px;
}

.searchable-select__clear {
  font-size: 1.25rem;
}

.searchable-select__chevron {
  transition: transform 0.15s ease;
}

.searchable-select__chevron:disabled {
  cursor: not-allowed;
}

.searchable-select--open .searchable-select__chevron {
  transform: rotate(180deg);
}

/* Default: directly below the control */
.searchable-select__menu {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 400;
  overflow: auto;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
}

/* Not enough space below: show above the control (same 2px gap) */
.searchable-select--up .searchable-select__menu {
  top: auto;
  bottom: calc(100% + 2px);
}

.searchable-select__option,
.searchable-select__empty {
  padding: 10px 14px;
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.87);
}

.searchable-select__option {
  cursor: pointer;
}

.searchable-select__option--active,
.searchable-select__option:hover {
  background: rgba(25, 118, 210, 0.08);
}

.searchable-select__empty {
  color: rgba(0, 0, 0, 0.5);
  cursor: default;
}
</style>
